import { useEffect, useMemo, useRef, useState } from "react";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
  type ColumnDef,
} from "@tanstack/react-table";
import { Area, AreaChart, ResponsiveContainer } from "recharts";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "../../ui/sheet";
import { TrendLineChart } from "./charts/trend-line-chart";
import { cn } from "../../utils/common/cn";
import type { SegmentException } from "../../pages/AthenaPricingEngine/lib/api/exceptions";

const pct = (value: number) => `${value.toFixed(2)}%`;

const helper = createColumnHelper<SegmentException>();

export function SegmentExceptionsTable({
  rows,
  focus,
}: {
  rows: SegmentException[];
  /** Bumped nonce + segment label: scrolls to and highlights that row. */
  focus?: { label: string; nonce: number } | null;
}) {
  const [selected, setSelected] = useState<SegmentException | null>(null);
  const [highlighted, setHighlighted] = useState<string | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!focus) return;
    setHighlighted(focus.label);
    const node = containerRef.current?.querySelector(
      `[data-segment-label="${focus.label}"]`,
    );
    node?.scrollIntoView({ behavior: "smooth", block: "center" });
    const timer = window.setTimeout(() => setHighlighted(null), 2400);
    return () => window.clearTimeout(timer);
  }, [focus?.label, focus?.nonce]);

  const columns = useMemo<ColumnDef<SegmentException, any>[]>(
    () => [
      helper.accessor("label", {
        header: "Segment",
        cell: (info) => <span className="font-medium text-foreground">{info.getValue()}</span>,
      }),
      helper.accessor("blendedRav", {
        header: "Blended RAV",
        cell: (info) => (
          <span className="font-semibold text-decline">{pct(info.getValue())}</span>
        ),
      }),
      helper.accessor("floor", { header: "Floor", cell: (info) => pct(info.getValue()) }),
      helper.display({
        id: "trend",
        header: "Trend",
        cell: ({ row }) => (
          <div className="ml-auto h-8 w-24">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={row.original.trend} margin={{ top: 2, right: 0, bottom: 0, left: 0 }}>
                <Area
                  type="monotone"
                  dataKey="value"
                  stroke="var(--decline)"
                  strokeWidth={1.5}
                  fill="var(--decline)"
                  fillOpacity={0.12}
                  isAnimationActive={false}
                  dot={false}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        ),
      }),
      helper.accessor("monthsBelowFloor", {
        header: "Months Below Floor",
        cell: (info) => info.getValue(),
      }),
      helper.accessor("primaryDriver", {
        header: "Primary Driver",
        cell: (info) => <span className="text-muted-foreground">{info.getValue()}</span>,
      }),
    ],
    [],
  );

  const table = useReactTable({
    data: rows,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  return (
    <>
      <div ref={containerRef} className="overflow-x-auto border border-border bg-card">
        <table className="w-full min-w-[860px] border-collapse text-sm">
          <thead>
            {table.getHeaderGroups().map((group) => (
              <tr key={group.id} className="border-b border-border bg-secondary/50">
                {group.headers.map((header, i) => (
                  <th
                    key={header.id}
                    className={cn(
                      "px-3 py-2.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground",
                      i === 0 || i === 5 ? "text-left" : "text-right",
                    )}
                  >
                    {flexRender(header.column.columnDef.header, header.getContext())}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.map((row) => (
              <tr
                key={row.id}
                data-segment-label={row.original.label}
                tabIndex={0}
                onClick={() => setSelected(row.original)}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    setSelected(row.original);
                  }
                }}
                className={cn(
                  "cursor-pointer border-b border-border/60 last:border-b-0 hover:bg-secondary/40 focus:bg-secondary/40 focus:outline-none",
                  highlighted === row.original.label && "bg-amber-dim ring-1 ring-primary",
                )}
              >
                {row.getVisibleCells().map((cell, i) => (
                  <td
                    key={cell.id}
                    className={cn(
                      "px-3 py-2.5 align-middle",
                      i === 0 || i === 5 ? "text-left" : "text-right tabular-nums",
                    )}
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Sheet open={selected != null} onOpenChange={(open) => !open && setSelected(null)}>
        <SheetContent className="w-full overflow-y-auto sm:max-w-xl">
          {selected ? (
            <>
              <SheetHeader>
                <SheetTitle className="text-base">{selected.label}</SheetTitle>
                <p className="text-xs text-muted-foreground">
                  Blended RAV {pct(selected.blendedRav)} against a {pct(selected.floor)} floor ·{" "}
                  {selected.monthsBelowFloor} months below floor
                </p>
              </SheetHeader>

              <div className="mt-6 border border-border bg-card p-4">
                <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                  Blended RAV by Month
                </p>
                <div className="mt-3">
                  <TrendLineChart
                    data={selected.ravByMonth}
                    height={240}
                    // Blended RAV is Athena green everywhere ELSE in the product; here
                    // it stays --decline red on purpose. This panel exists to show a
                    // segment sitting BELOW its floor, and green reads as healthy --
                    // the one place where the product-wide colour would contradict what
                    // the chart is for.
                    series={[
                      { dataKey: "blendedRav", name: "Blended RAV", color: "var(--decline)" },
                      { dataKey: "floor", name: "Floor", color: "var(--neutral-inactive)", dashed: true },
                    ]}
                  />
                </div>
              </div>

              <div className="mt-6 border border-border bg-card">
                <p className="border-b border-border px-4 py-2.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                  What Is Dragging the Average Down
                </p>
                <ul className="divide-y divide-border/60">
                  {selected.vintageDrag.map((item) => (
                    <li key={item.vintage} className="px-4 py-3">
                      <div className="flex items-baseline justify-between gap-3">
                        <span className="text-sm font-medium text-foreground">
                          {item.vintage} vintage
                        </span>
                        <span className="text-sm font-semibold tabular-nums text-decline">
                          {pct(item.contribution)}
                        </span>
                      </div>
                      <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
                        {item.note}
                      </p>
                    </li>
                  ))}
                </ul>
              </div>
            </>
          ) : null}
        </SheetContent>
      </Sheet>
    </>
  );
}
